import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import axios from "axios";

/**
 * Student Status Summary Report
 * Purpose: summarize student counts by status and enrollment for dashboards
 * Notes / assumptions:
 * - Expects backend endpoints for JSON data at
 *   /api/student-reports/:clientId/:collegeId/status-summary (returns array of rows)
 * - CSV/PDF download endpoints used:
 *   /api/student-reports/:clientId/:collegeId/download/status/csv
 *   /api/student-reports/:clientId/:collegeId/download/status/pdf
 * If your backend endpoint names differ, update the URLs below accordingly.
 */
export default function StudentEnrollmentHealth() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);

  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchBranches = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/branches/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (resp.data && resp.data.data && Array.isArray(resp.data.data)) setBranches(resp.data.data);
      else setBranches([]);
    } catch (err) {
      console.error("Error fetching branches", err);
      setBranches([]);
    }
  }, [user]);

  const fetchBatches = useCallback(async () => {
    if (!user) return;
    try {
      const branchQuery = selectedBranch ? `?branchId=${selectedBranch}` : "";
      const url = `${API_BASE_URL}/api/admittedbatches/${user.client_id}/${user.college_id}${branchQuery}`;
      const resp = await axios.get(url, { headers: getAuthHeaders() });
      if (Array.isArray(resp.data)) setBatches(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setBatches(resp.data.data);
      else setBatches([]);
    } catch (err) {
      console.error("Error fetching batches", err);
      setBatches([]);
    }
  }, [user, selectedBranch]);

  // Fetch summary data (assumes backend provides a status-summary JSON endpoint)
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (search && search.trim() !== "") params.append("search", search.trim());

      const queryString = params.toString();
      // Server provides status summary at /summary/status
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/summary/status`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const resp = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(resp.data)) data = resp.data;
      else if (resp.data && Array.isArray(resp.data.data)) data = resp.data.data;

      // Ensure each row has an id for DataGrid
      const formatted = data.map((r, i) => ({ id: r.id || i, ...r }));
      setRows(formatted);
    } catch (err) {
      console.error("Error fetching status summary", err);
      showSnackbar("Error fetching status summary", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, selectedBranch, selectedBatch, search]);

  // Download CSV
  const handleDownloadCsv = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/status/csv`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_status_summary.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded successfully", "success");
    } catch (err) {
      console.error("Error downloading CSV", err);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/status/pdf`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_status_summary.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("PDF downloaded successfully", "success");
    } catch (err) {
      console.error("Error downloading PDF", err);
      showSnackbar("Error downloading PDF", "error");
    }
  };

  useEffect(() => {
    if (user) {
      fetchBranches();
      fetchData();
    }
  }, [user, fetchBranches, fetchData]);

  useEffect(() => {
    if (user) fetchBatches();
  }, [user, selectedBranch, fetchBatches]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFilterClick = (e) => setFilterAnchorEl(e.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearFilters = () => {
    setSelectedBranch("");
    setSelectedBatch("");
  };

  const columns = [
    {
      field: "branch_name",
      headerName: "Branch Name",
      flex: 1.5,
      minWidth: 160,
      valueGetter: (params) => params.row.branch_name || params.row.branch?.branch_name || "",
    },
    {
      field: "admitted_batch",
      headerName: "Admitted Batch",
      flex: 1.2,
      minWidth: 140,
      // backend returns `admittedbatch` (no underscore) or nested batch.admittedbatch
      valueGetter: (params) =>
        params.row.admittedbatch ||
        params.row.batch?.admittedbatch ||
        params.row.admitted_batch ||
        "",
    },
    { field: "total_students", headerName: "Total Students", flex: 0.9, minWidth: 120 },
    { field: "active_students", headerName: "Active Students", flex: 0.9, minWidth: 120 },
    { field: "passed_out_students", headerName: "Passed Out", flex: 0.9, minWidth: 120 },
    { field: "dropped_tc_students", headerName: "Dropped / TC", flex: 0.9, minWidth: 120 },
    { field: "enrolled_students", headerName: "Enrolled Students", flex: 0.9, minWidth: 120 },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            STUDENT STATUS SUMMARY REPORT
          </Typography>

          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            onClick={handleMenuOpen}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            ACTIONS
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#115293",
                color: "#115293",
              },
            }}
          >
            FILTERS
            {(selectedBatch || selectedBranch) && (
              <Chip
                size="small"
                label={(selectedBatch ? 1 : 0) + (selectedBranch ? 1 : 0)}
                sx={{ ml: 1, backgroundColor: "#1976d2", color: "white" }}
              />
            )}
          </Button>

          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsv();
                }}
                sx={{ backgroundColor: "#4caf50", color: "#fff" }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdf();
                }}
                sx={{ backgroundColor: "#e53935", color: "#fff" }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
          </Menu>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                background: "#ffffff",
                minWidth: 240,
                maxWidth: 320,
              },
            }}
          >
            <Box sx={{ p: 1.5, background: "#ffffff" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    p: 0.5,
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Branch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBranch || ""}
                  onChange={(e) => {
                    setSelectedBranch(e.target.value);
                    setSelectedBatch("");
                  }}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Branches --</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>
                      {b.branch_name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Admitted Batch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBatch || ""}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Batches --</option>
                  {batches.map((batch) => (
                    <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                      {batch.admittedbatch}
                    </option>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={fetchData}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
